import {calculateAndUpdateProfileReps} from '@/api/api';
import {useGetUser} from '@/api/User/useGetUser';
import {useProfiles} from '@/api/User/useProfiles';
import Loader from '@/components/MLS/Loader/Loader';
import {useMutation} from '@tanstack/react-query';
import {memo, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {Leaderboard} from './Leaderboard/Leaderboard';
import {MySessions} from './MySessions/MySessions';
import {PlayerCard} from './PlayerCard/PlayerCard';

export const PlayerHome = memo(() => {
  const navigate = useNavigate();
  const {
    currentProfile: {_id: currentProfileId, playerCardInfo, jerseyInfo, lifeTimeReps},
    refetchUserAndProfiles,
  } = useProfiles();
  const currentActiveProfileId = useRef<string | null>(null);
  const {isFetching: isUserAndProfilesFetching} = useGetUser({enabled: false});

  // Mutation for calculating and updating profile reps
  const {mutate: calculateReps, isLoading: isCalculatingReps} = useMutation({
    mutationFn: () => calculateAndUpdateProfileReps(),
    onSuccess: () => {
      refetchUserAndProfiles();
    },
  });

  // Only trigger once if lifeTimeReps is missing
  const hasTriggeredRepsCalc = useRef(false);
  useEffect(() => {
    if (
      !hasTriggeredRepsCalc.current &&
      currentProfileId &&
      (typeof lifeTimeReps !== 'number' || lifeTimeReps === null || lifeTimeReps === undefined)
    ) {
      hasTriggeredRepsCalc.current = true;
      calculateReps();
    }
  }, [currentProfileId, lifeTimeReps, calculateReps]);

  useEffect(() => {
    if (currentProfileId !== currentActiveProfileId.current && !isUserAndProfilesFetching) {
      currentActiveProfileId.current = currentProfileId;
      if (!playerCardInfo || !(playerCardInfo?.selectedCartoonIndex || playerCardInfo?.selectedCartoonIndex === 0)) {
        navigate('/avatar-creator');
      } else if (!jerseyInfo || !jerseyInfo?.jerseyName) {
        navigate('/customize-jersey');
      }
    }
  }, [currentProfileId, navigate, isUserAndProfilesFetching]);

  return isUserAndProfilesFetching || isCalculatingReps ? (
    <div className="flex min-h-screen items-center justify-center">
      <Loader text="Loading..." />
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center gap-8 py-8" data-test-id="player-home-container">
      <PlayerCard containerClassNames="w-[327px] h-[530px] cursor-pointer" />
      <Leaderboard />
      {/* <PersonalBest /> */}
      <MySessions />
    </div>
  );
});

PlayerHome.displayName = 'PlayerHome';
